import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Timer, CalendarDays, Flame, Gift } from 'lucide-react';
import {
  C, fontSerif, fontMono, todayStr, addDays,
  workingDaysBetween, currencySymbol,
} from './utils';

const pad = (n) => String(n).padStart(2, '0');

export const HeroCountdownTimer = ({ period, calendar, reward }) => {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  if (!period) return null;

  const ww = calendar?.working_weekdays || [0, 1, 2, 3, 4];
  const holidays = (calendar?.holidays || []).map((h) => h.date);
  const today = todayStr();

  const start = period.start_date;
  const end = period.end_date;

  const totalWorking = workingDaysBetween(start, end, ww, holidays);
  const fromTomorrow = today < end ? addDays(today > start ? today : start, 1) : end;
  const workingLeft = today <= end
    ? workingDaysBetween(today > start ? today : start, end, ww, holidays)
    : 0;

  // calendar days
  const endMs = new Date(end + 'T23:59:59').getTime();
  const startMs = new Date(start + 'T00:00:00').getTime();
  const totalMs = endMs - startMs;
  const elapsedMs = Math.min(Math.max(now - startMs, 0), totalMs);
  const pctElapsed = totalMs > 0 ? Math.round((elapsedMs / totalMs) * 100) : 0;

  const remainMs = Math.max(endMs - now, 0);
  const days = Math.floor(remainMs / 86400000);
  const hours = Math.floor((remainMs % 86400000) / 3600000);
  const mins = Math.floor((remainMs % 3600000) / 60000);
  const secs = Math.floor((remainMs % 60000) / 1000);

  const stat = (value, label, Icon, tint) => (
    <div className="flex items-center gap-3 rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.06)' }}>
      <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ background: tint }}>
        <Icon className="h-4 w-4 text-white" strokeWidth={2} />
      </div>
      <div>
        <div className="text-xl font-bold leading-none text-white" style={{ fontFamily: fontMono }}>{value}</div>
        <div className="text-[11px] uppercase tracking-wider text-white/50 mt-1">{label}</div>
      </div>
    </div>
  );

  const seg = (value, label) => (
    <div className="flex flex-col items-center">
      <div
        className="rounded-lg px-3 py-2 text-3xl font-bold tabular-nums text-white sm:text-4xl"
        style={{ fontFamily: fontMono, background: 'rgba(255,255,255,0.08)', minWidth: '3.2rem' }}
        data-testid={`countdown-${label.toLowerCase()}`}
      >
        {pad(value)}
      </div>
      <span className="mt-1 text-[10px] uppercase tracking-widest text-white/50">{label}</span>
    </div>
  );

  const sym = currencySymbol(reward?.currency);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="relative overflow-hidden rounded-2xl p-6 sm:p-8"
      style={{ background: `linear-gradient(135deg, ${C.burgundy} 0%, #5A1329 100%)` }}
      data-testid="countdown-hero"
    >
      <div
        className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20"
        style={{ background: `radial-gradient(circle, ${C.gold}, transparent 70%)` }}
      />
      <div className="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-white/70">
            <Timer className="h-4 w-4" />
            <span className="text-xs uppercase tracking-widest">{period.name} · time remaining</span>
          </div>
          <div className="mt-3 flex items-end gap-2">
            {seg(days, 'Days')}
            <span className="pb-3 text-2xl text-white/40">:</span>
            {seg(hours, 'Hrs')}
            <span className="pb-3 text-2xl text-white/40">:</span>
            {seg(mins, 'Min')}
            <span className="pb-3 text-2xl text-white/40">:</span>
            {seg(secs, 'Sec')}
          </div>
          {reward?.amount ? (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm text-white/90">
              <Gift className="h-4 w-4" style={{ color: C.gold }} />
              Unlock a {sym}{reward.amount.toLocaleString('en-IN')} {reward.name} this period
            </div>
          ) : null}
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {stat(workingLeft, 'Working days left', CalendarDays, 'rgba(99,102,241,0.5)')}
          {stat(totalWorking, 'Working days total', Flame, 'rgba(212,175,55,0.5)')}
          <div className="rounded-xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.06)' }} data-testid="period-progress-bar">
            <div className="flex items-center justify-between text-white">
              <span className="text-xl font-bold" style={{ fontFamily: fontMono }}>{pctElapsed}%</span>
              <span className="text-[11px] uppercase tracking-wider text-white/50">elapsed</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-white/15">
              <div className="h-full rounded-full" style={{ width: `${pctElapsed}%`, background: C.gold }} />
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};
